<template>
  <el-form
    v-loading="loading"
    :model="useCaseRegistrationForm"
    :rules="rules"
    autocomplete="off"
    label-position="top"
    ref="useCaseRegistrationForm"
  >
    <div class="row">
      <div class="col-md-12">
        <h5>A2P Use Case Registration</h5>
        <div class="dropdown-divider" />

        <el-alert
          v-if="!selected_messaging_service?.a2p_use_case"
          title="Important"
          class="mb-2"
          description="Purposefully misleading your campaign will result in violations and possible termination."
          type="info"
          show-icon
        />

        <!-- Campaign Status Title -->
        <el-alert
          v-if="hasCampaignIssues"
          type="error"
          show-icon
          :closable="false"
        >
          <template slot="title">
            <span class="font-weight-bold">{{ campaignStatusTitle }}</span>
          </template>
        </el-alert>

        <!-- Campaign Details -->
        <el-alert
          v-if="hasCampaignIssues"
          type="error"
          :closable="false"
          class="mb-3 text-black"
        >
          <!-- Pre-vetting failure reason -->
          <div
            v-if="selected_messaging_service?.a2p_use_case?.failure_reason"
            class="mb-3"
          >
            <strong class="text-black">Pre-vetting Failure:</strong>
            <p class="mb-0 text-black">
              {{ selected_messaging_service?.a2p_use_case?.failure_reason }}
            </p>
          </div>

          <!-- Error Details -->
          <div
            v-if="selected_messaging_service?.a2p_use_case?.errors?.length"
            class="mb-3"
          >
            <strong class="text-black">Error Details:</strong>
            <ul class="mb-0 mt-1">
              <li
                class="text-black"
                v-for="error in selected_messaging_service?.a2p_use_case
                  ?.errors"
                :key="error.error_code"
              >
                <strong>{{ fixToUcFirst(error.fields[0]) }}</strong> (Code:
                {{ error.error_code }}): {{ error.description }}
              </li>
            </ul>
          </div>

          <!-- Financial Information -->
          <div class="mb-3">
            <strong class="text-black">Financial Information:</strong>
            <ul class="mb-0 mt-1">
              <li class="text-black">
                <strong>Monthly Charges:</strong>
                {{ monthlyChargeAmount }} monthly recurring charges will
                continue to apply
              </li>
              <li class="text-black" v-if="!hasIneligibleErrors">
                <strong>Resubmission Fee:</strong>
                <span
                  v-if="
                    selected_messaging_service?.a2p_use_case?.tcr_campaign_id
                  "
                >
                  $15 USD will be charged for resubmission
                </span>
                <span v-else> Free resubmission (no additional charge) </span>
              </li>
            </ul>
          </div>

          <!-- Next Steps -->
          <div>
            <strong class="text-black">Next Steps:</strong>
            <p class="mb-0 mt-1">
              <span class="text-black" v-if="hasIneligibleErrors">
                Create a new campaign with compliant content or contact support.
              </span>
              <span class="text-black" v-else>
                Review the error details above, make the necessary adjustments,
                and resubmit the campaign.
              </span>
            </p>
          </div>
        </el-alert>

        <el-form-item
          label="Use Case"
          prop="use_case"
          data-aloai-field="use_case"
        >
          <span class="page-anchor" />
          <p class="line-height-normal">
            Read our
            <a
              class="font-weight-bold text-blue"
              target="_blank"
              href="https://support.aloware.com/en/articles/9031988-a2p-10dlc-compliance-campaign-registration"
              >Easy Guide</a
            >
            on what Use Case to choose.
          </p>

          <div class="switch-label">
            <el-radio-group
              v-model="useCaseRegistrationForm.use_case"
              :disabled="shouldDisableForm || isSuspendedOrRejected"
              @change="useCaseChanged()"
            >
              <el-radio
                v-for="use_case in selectableUseCases"
                :key="use_case.value"
                :label="use_case.value"
                class="d-flex"
              >
                <div>
                  <span class="font-weight-bold">{{ use_case.title }}</span
                  >: {{ use_case.description }}
                </div>
              </el-radio>
            </el-radio-group>
          </div>
        </el-form-item>

        <el-form-item label="Unregistered Lines">
          <span class="page-anchor" />
          <p class="line-height-normal">
            You can register up to
            {{ selected_messaging_service.max_pool_size }} numbers on a single
            campaign.
          </p>
          <campaign-selector
            multiple
            :collapsable="shouldCollapseLines"
            clearable
            only_10_dlc
            :value="line_ids"
            :is_a2p_campaign_wizard="true"
            :show_paused="true"
            :unregistered_lines_only="true"
            :v-if="shouldShowUnregisteredLines"
            @change="addLine"
            @input="addLine"
          />
        </el-form-item>

        <el-form-item
          label="Campaign Description"
          prop="description"
          class="pb-2"
          data-aloai-field="description"
        >
          <span class="page-anchor" />
          <p class="line-height-normal">
            Describe what you will be using this campaign for including the
            campaign's purpose, audience and sender.
            <a
              class="font-weight-bold text-blue"
              target="_blank"
              href="https://support.aloware.com/en/articles/9031981-a2p-10dlc-compliance-brand-registration-guide"
              >Learn more</a
            >
          </p>

          <el-input
            type="textarea"
            maxlength="4096"
            show-word-limit
            :autosize="{ minRows: 3 }"
            v-model="useCaseRegistrationForm.description"
            :disabled="shouldDisableForm"
            @input="preValidateForm('useCaseRegistrationForm')"
          />
          <p class="line-height-normal mb-0">
            <b>* Example</b>:
            <span class="font-italic"
              >Provide assistance to our current customers, mainly
              conversational communication.</span
            >
          </p>
        </el-form-item>

        <el-form-item
          prop="message_flow"
          label="How do end-users consent to receive messages?"
          class="pb-2"
          data-aloai-field="message_flow"
        >
          <span class="page-anchor" />
          <p class="line-height-normal">
            Describe how users opt-in to your campaign, therefore giving consent
            to the sender to receive their messages.
          </p>

          <el-input
            type="textarea"
            maxlength="2048"
            show-word-limit
            :autosize="{ minRows: 3 }"
            v-model="useCaseRegistrationForm.message_flow"
            :disabled="shouldDisableForm"
            @input="preValidateForm('useCaseRegistrationForm')"
          />
          <p class="line-height-normal mb-0">
            <b>* Example</b>:
            <span class="font-italic"
              >End users opt-in by visiting www.examplewebsite.com and adding
              their number. They then check a box agreeing to receive text
              messages from Example Brand. Additionally, end users can also
              opt-in by texting START to (111) 222-3333 to opt-in.</span
            ><span v-popover:popover class="font-weight-bold text-blue ml-1"
              >Learn more</span
            >
          </p>

          <el-popover
            ref="popover"
            placement="top-start"
            title="How do end-users consent to receive messages?"
            width="800"
            trigger="hover"
          >
            <p class="line-height-normal">
              This field should describe how and when consumers opt-in to the
              campaign, therefore giving consent to the sender to receive their
              messages. The call-to-action must be explicitly clear and inform
              consumers of the nature of the program. If multiple opt-in methods
              can be used for the same, list them all.
            </p>
            <ul>
              <li>Provide website where opt-in occurs</li>
              <li>List all opt-in methods</li>
              <li>Include an opt-in message if users can text</li>
              <li>
                If a website is provided, it must be accessible with privacy
                policy and terms
              </li>
            </ul>
            <p class="line-height-normal">
              <span class="font-weight-bold">Successful Example:</span>
              "Customers provide opt-in specifically when enrolling into our
              website, or in person by requesting SMS capabilities within the
              store. Opt-in during website is a self-service process and occurs
              at acme.com/signup. By submitting, you authorize ACME to send text
              messages with offers and other information. Message/data rates
              apply. Consent is not a condition of purchase. [Link to terms and
              conditions]."
            </p>
            <p class="line-height-normal">
              <span class="font-weight-bold">Failure Example:</span> "Customers
              sign up" (Where and how the customer provides opt-in is unclear,
              campaign will be rejected.)
            </p>
          </el-popover>
        </el-form-item>

        <el-form-item
          prop="privacy_policy_url"
          label="Link to Privacy Policy"
          class="pb-2"
        >
          <span class="page-anchor" />
          <el-input
            v-model="useCaseRegistrationForm.privacy_policy_url"
            placeholder="https://example.com/privacy"
            :disabled="shouldDisableForm"
            @input="preValidateForm('useCaseRegistrationForm')"
            @blur="() => { privacy_policy_url = privacy_policy_url; preValidateForm('useCaseRegistrationForm'); }"
          />
        </el-form-item>

        <el-form-item
          prop="terms_and_conditions_url"
          label="Link to Terms and Conditions"
          class="pb-2"
        >
          <span class="page-anchor" />
          <el-input
            v-model="useCaseRegistrationForm.terms_and_conditions_url"
            placeholder="https://example.com/terms"
            :disabled="shouldDisableForm"
            @input="preValidateForm('useCaseRegistrationForm')"
            @blur="() => { terms_and_conditions_url = terms_and_conditions_url; preValidateForm('useCaseRegistrationForm'); }"
          />
        </el-form-item>

        <el-form-item
          prop="sample_message_1"
          class="pb-2"
          data-aloai-field="sample_message_1"
        >
          <template slot="label">
            Sample Message #1
            <el-tooltip
              class="item"
              effect="dark"
              content="Click to pick a random message from your Outbound SMS"
              placement="right"
            >
              <i
                class="material-icons hover-lower-opacity-primary text-primary"
                @click="randomizeCommunicationSamples('sample_message_1')"
                >sync</i
              >
            </el-tooltip>
          </template>
          <span class="page-anchor" />
          <p class="line-height-normal">
            Make sure to include compliant opt-out language in your sample
            messages, such as <i>Text STOP to unsubscribe</i>.
            <b class="text-primary"
              >Failure to include opt-out language will result in failed
              registration.</b
            >
          </p>

          <el-input
            type="textarea"
            maxlength="1024"
            show-word-limit
            :autosize="{ minRows: 3 }"
            v-model="useCaseRegistrationForm.sample_message_1"
            :disabled="shouldDisableForm"
            @input="preValidateForm('useCaseRegistrationForm')"
          />
          <p class="line-height-normal mb-0">
            <b>* Example</b>:
            <span class="font-italic"
              >Hi {Customer Name}, this is {Agent Name} from {Company Name}.
              Thank you for reaching out. Reply with your question or call us at
              {Phone Number}. For account updates, visit {Company Website}.
              Reply STOP to opt-out.</span
            >
          </p>
        </el-form-item>

        <el-form-item
          prop="sample_message_2"
          class="pb-2"
          data-aloai-field="sample_message_2"
        >
          <template slot="label">
            Sample Message #2
            <el-tooltip
              class="item"
              effect="dark"
              content="Click to pick a random message from your Outbound SMS"
              placement="right"
            >
              <i
                class="material-icons hover-lower-opacity-primary text-primary"
                @click="randomizeCommunicationSamples('sample_message_2')"
                >sync</i
              >
            </el-tooltip>
          </template>
          <span class="page-anchor" />
          <p class="line-height-normal">
            Please provide another example of the message that you intend to
            send within this campaign.
          </p>

          <el-input
            type="textarea"
            maxlength="1024"
            show-word-limit
            :autosize="{ minRows: 3 }"
            v-model="useCaseRegistrationForm.sample_message_2"
            :disabled="shouldDisableForm"
            @input="preValidateForm('useCaseRegistrationForm')"
          />
          <p class="line-height-normal mb-0">
            <b>* Example</b>:
            <span class="font-italic"
              >🌟 Exclusive Offer from {Company Name}! Unlock 20% off your next
              purchase with code WELCOME20. Shop now at {Company Website}. Reply
              STOP to opt-out.</span
            >
          </p>
        </el-form-item>

        <el-form-item
          prop="message_contents"
          label="Message Contents"
          data-aloai-field="has_embedded_links"
        >
          <span class="page-anchor" />

          <el-checkbox
            v-model="useCaseRegistrationForm.has_embedded_links"
            :disabled="shouldDisableForm"
          >
            Messages will include embedded links.
          </el-checkbox>
          <el-checkbox
            v-model="useCaseRegistrationForm.has_embedded_phone"
            :disabled="shouldDisableForm"
          >
            Messages will include phone numbers.
          </el-checkbox>
        </el-form-item>

        <el-form-item prop="message_restrictions" label="Message Restrictions">
          <span class="page-anchor" />
          <el-checkbox
            v-model="useCaseRegistrationForm.direct_lending"
            :disabled="shouldDisableForm"
          >
            Message will include content related to Direct Lending or other Loan
            Arrangement.
          </el-checkbox>
          <br />
          <small class="text-muted"
            >High Risk Loans, Indirect Loans and Debt Collection are prohibited
            under A2P 10DLC Guidelines</small
          >
          <br />
          <el-checkbox
            v-model="useCaseRegistrationForm.age_gated"
            :disabled="shouldDisableForm"
          >
            Messages will include age-gated content.
          </el-checkbox>
          <br />
          <small class="text-muted"
            >Sex, Hate, Alcohol, Firearms, Tobacco (SHAFT) and Cannabis content
            are prohibited under A2P 10DLC and
            <a
              class="font-weight-bold text-blue"
              target="_blank"
              href="https://www.ctia.org/the-wireless-industry/industry-commitments/messaging-interoperability-sms-mms"
              >CTIA Guidelines</a
            ></small
          >
          <br />
        </el-form-item>

        <el-form-item
          prop="accepted_campaign_fee"
          label="One-time Per Campaign Fee"
          class="pb-2"
        >
          <p class="form-label mb-0">
            A non-refundable one-time fee of $15 is charged to manually vet each
            A2P Campaign. The manual vetting process is an industry standard to
            ensure the quality of messages through A2P 10DLC registered
            channels.
          </p>
          <el-checkbox
            label="I agree to a one-time non-refundable $15 fee."
            v-model="useCaseRegistrationForm.accepted_campaign_fee"
            :disabled="shouldDisableForm || isSuspendedOrRejected"
            @change="preValidateForm('useCaseRegistrationForm')"
          />
        </el-form-item>

        <el-form-item
          prop="agree_terms_and_conditions"
          label="Agree Terms and Conditions"
        >
          <span class="page-anchor" />
          <el-checkbox
            label="I agree to the terms and conditions and monthly recurring $10 carrier fee."
            v-model="useCaseRegistrationForm.agree_terms_and_conditions"
            :disabled="shouldDisableForm || isSuspendedOrRejected"
            @change="preValidateForm('useCaseRegistrationForm')"
          />

          <el-popover
            ref="agree_terms_and_conditions_learn_more"
            placement="right-start"
            title="Do you want to add an additional campaign?"
            width="400"
            trigger="hover"
            content="The allowed message volume depends on the trust score of your business. Ranges can start from 2k - 10k per day. Your messages will be capped once you reach your daily limit. Limits may be increased as your brand score improves."
          >
          </el-popover>
          <span
            class="form-hover-help"
            v-popover:agree_terms_and_conditions_learn_more
          >
            Learn more
          </span>
        </el-form-item>

        <div class="row w-full">
          <div class="col-md-8 mb-3 mb-md-0 flex-3">
            <alert-message :alert-message="alertMessage" @alert="submitAlert" />
          </div>
          <div class="w-full flex-1 d-flex align-items-center" style="gap: 8px">
            <el-button
              class="aloai-analyze-btn"
              style="flex: 1"
              :loading="aiPanel.loading"
              :disabled="!validated || shouldDisableForm || isAnalyzeDisabled"
              @click="runAiPreCheck"
            >
              <span
                v-if="!aiPanel.loading"
                class="d-flex align-items-center justify-content-center"
                style="gap: 6px"
              >
                <sparkle-bold-icon width="14" height="14" color="#fff" />
                {{ analyzeButtonLabel }}
              </span>
              <span v-else>Analyzing…</span>
            </el-button>
            <el-tooltip
              :content="finishDisabledTooltip"
              :disabled="!isFinishDisabled"
              placement="top"
            >
              <span style="flex: 1; display: block">
                <el-button
                  type="success"
                  @click="openUseCaseRegistrationConfirmation"
                  class="w-full"
                  :loading="loading"
                  :disabled="
                    !validated || shouldDisableForm || isFinishDisabled
                  "
                >
                  Finish <i class="el-icon-arrow-right el-icon-right"></i>
                </el-button>
              </span>
            </el-tooltip>
          </div>
        </div>
      </div>
    </div>
    <a2p-ai-check-panel
      :visible="aiPanel.visible"
      :loading="aiPanel.loading"
      :result="aiPanel.result"
      :error="aiPanel.error"
      :error-message="aiPanel.errorMessage"
      :form-data="useCaseRegistrationForm"
      @continue="handleAiContinue"
      @dismiss="handleAiDismiss"
      @apply-fix="handleAiApplyFix"
      @undo-fix="handleAiUndoFix"
      @update:all-resolved="aiPanel.allResolved = $event"
      @retry="runAiPreCheck"
    />
  </el-form>
</template>

<script>
import { mapState } from 'vuex'
import {
  compliance_mixin,
  form_validation_mixin,
  campaign_mixin
} from '../../../../mixins'
import A2pAiCheckPanel from './a2p-ai-check-panel.vue'
import SparkleBoldIcon from '../../../../../images/app-icons/sparkle-bold-icon.vue'
import AlertMessage from './alert-message.vue'
import CampaignSelector from '../../../campaign-selector.vue'
import {
  USE_CASES as UseCases,
  USE_CASE_CUSTOMER_CARE,
  USE_CASE_MARKETING,
  USE_CASE_MIXED,
  USE_CASE_LOW_VOLUME,
  INELIGIBLE_ERROR_CODES
} from '../../../../constants/compliance/a2p-use-cases'
import * as ComplianceStatuses from '../../../../constants/compliance-bundle-statuses'

export default {
  name: 'UseCaseRegistration',
  mixins: [compliance_mixin, form_validation_mixin, campaign_mixin],
  components: {
    A2pAiCheckPanel,
    SparkleBoldIcon,
    AlertMessage,
    CampaignSelector
  },
  props: {
    selected_lines: {
      type: Array,
      required: true
    },
    selected_messaging_service: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      UseCases,
      ComplianceStatuses,
      line_ids: [],
      useCaseRegistrationForm: {
        line_ids: [],
        accepted_campaign_fee: false,
        use_case: '',
        description: '',
        sample_message_1: '',
        sample_message_2: '',
        has_embedded_links: false,
        has_embedded_phone: false,
        age_gated: false,
        direct_lending: false,
        agree_terms_and_conditions: false,
        message_flow: '',
        privacy_policy_url: '',
        terms_and_conditions_url: ''
      },
      rules: {
        line_ids: [
          {
            validator: (rule, value, callback) => {
              /** @const {array} selected_lines */
              const selected_lines = this.campaigns.filter((line) =>
                this.useCaseRegistrationForm.line_ids.includes(line.id)
              )

              // Sanity check
              if (selected_lines.length === 0) {
                callback(new Error('Please select at least one line.'))
                return
              }

              /** @const {boolean} has_local_presence */
              const has_local_presence = selected_lines.some(
                (line) => line.has_local_presence
              )

              /** @const {boolean} has_local_presence */
              const has_regular_lines = selected_lines.some(
                (line) => !line.has_local_presence
              )

              // If the selected lines have both local presence and regular lines, throw an error
              if (
                has_local_presence &&
                has_regular_lines &&
                this.isCompanyNetNew
              ) {
                callback(
                  new Error(
                    'You cannot attach both Local Presence and Regular Lines to the same A2P Messaging Service.'
                  )
                )
                return
              }

              /** @const {int} total_incoming_numbers - Only count 10 DLC Numbers from the selected lines */
              const total_incoming_numbers = selected_lines.reduce(
                (total, line) => {
                  return total + line.incoming_10_dlc_numbers.length
                },
                0
              )

              // If the total incoming numbers is greater than the current pool size, throw an error
              if (
                total_incoming_numbers >
                this.selected_messaging_service.max_pool_size
              ) {
                callback(
                  new Error(
                    `Selected lines have more than ${this.selected_messaging_service.max_pool_size} numbers and it will exceed the pool size limit.`
                  )
                )
              } else {
                callback()
              }
            },
            type: 'array',
            trigger: 'change'
          }
        ],
        accepted_campaign_fee: [
          {
            validator: (rule, value, callback) => {
              if (value === true) {
                callback()
              } else {
                callback(
                  new Error('Please agree to a one-time $15 per campaign fee.')
                )
              }
            },
            trigger: 'change'
          }
        ],
        message_flow: [
          {
            required: true,
            message: 'Please provide a message flow.',
            trigger: 'blur'
          },
          {
            min: 40,
            message: 'Message flow is too short'
          },
          {
            max: 2048,
            message: 'Message flow is too long'
          }
        ],
        terms_and_conditions_url: [
          {
            required: true,
            message: 'Please provide a Terms and Conditions URL.',
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (typeof value !== 'string' || !value.startsWith('https://')) {
                callback(new Error('Terms and Conditions URL must start with https://'))
              } else if (value === 'https://') {
                callback(new Error('Terms and Conditions URL must include a domain after https://'))
              } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                callback(new Error('Terms and Conditions URL must be a valid URL'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        privacy_policy_url: [
          {
            required: true,
            message: 'Please provide a Privacy Policy URL.',
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (typeof value !== 'string' || !value.startsWith('https://')) {
                callback(new Error('Privacy Policy URL must start with https://'))
              } else if (value === 'https://') {
                callback(new Error('Privacy Policy URL must include a domain after https://'))
              } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                callback(new Error('Privacy Policy URL must be a valid URL'))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ],
        use_case: [
          {
            required: true,
            message: 'Please provide a use case.',
            trigger: 'blur'
          }
        ],
        description: [
          {
            required: true,
            message: 'Please provide a description.',
            trigger: 'blur'
          },
          {
            min: 40,
            message: 'Description is too short'
          },
          {
            max: 4096,
            message: 'Description is too long'
          }
        ],
        sample_message_1: [
          {
            required: true,
            message: 'Please provide a sample message (#1).',
            trigger: 'blur'
          },
          {
            min: 20,
            message: 'Sample message is too short'
          },
          {
            max: 1024,
            message: 'Sample message is too long'
          }
        ],
        sample_message_2: [
          {
            required: true,
            message: 'Please provide a sample message (#2).',
            trigger: 'blur'
          },
          {
            min: 20,
            message: 'Sample message is too short'
          },
          {
            max: 1024,
            message: 'Sample message is too long'
          }
        ],
        has_embedded_links: [{ type: 'boolean' }],
        has_embedded_phone: [{ type: 'boolean' }],
        age_gated: [{ type: 'boolean' }],
        direct_lending: [{ type: 'boolean' }],
        agree_terms_and_conditions: [
          {
            validator: (rule, value, callback) => {
              if (value === true) {
                callback()
              } else {
                callback(new Error('Please agree terms and conditions.'))
              }
            },
            trigger: 'change'
          }
        ]
      },
      communications_messages: [],
      aiPanel: {
        visible: false,
        loading: false,
        result: null,
        error: false,
        errorMessage: null,
        analyzed: false,
        hasIssues: false,
        allResolved: false
      },
      formChangedSinceAnalysis: false,
      pendingNextData: null
    }
  },
  mounted() {
    if (this.selected_messaging_service?.a2p_use_case) {
      // Pre-populate the rest o the fields
      this.useCaseRegistrationForm.use_case =
        this.selected_messaging_service?.a2p_use_case?.use_case
      this.useCaseRegistrationForm.description =
        this.selected_messaging_service?.a2p_use_case?.description
      this.useCaseRegistrationForm.message_flow =
        this.selected_messaging_service?.a2p_use_case?.message_flow
      this.useCaseRegistrationForm.terms_and_conditions_url =
        this.selected_messaging_service?.a2p_use_case?.terms_and_conditions_url
      this.useCaseRegistrationForm.privacy_policy_url =
        this.selected_messaging_service?.a2p_use_case?.privacy_policy_url
      this.useCaseRegistrationForm.sample_message_1 =
        this.selected_messaging_service?.a2p_use_case?.message_samples[0]
      this.useCaseRegistrationForm.sample_message_2 =
        this.selected_messaging_service?.a2p_use_case?.message_samples[1]
      this.useCaseRegistrationForm.has_embedded_links =
        this.selected_messaging_service?.a2p_use_case?.has_embedded_links
      this.useCaseRegistrationForm.has_embedded_phone =
        this.selected_messaging_service?.a2p_use_case?.has_embedded_phone
      this.useCaseRegistrationForm.age_gated =
        this.selected_messaging_service?.a2p_use_case?.age_gated
      this.useCaseRegistrationForm.direct_lending =
        this.selected_messaging_service?.a2p_use_case?.direct_lending
      this.useCaseRegistrationForm.accepted_campaign_fee =
        this.selected_messaging_service?.a2p_use_case?.accepted_campaign_fee
      this.useCaseRegistrationForm.agree_terms_and_conditions =
        this.selected_messaging_service?.a2p_use_case?.agree_terms_and_conditions
    }

    VueEvent.listen(
      'randomize_communication_outbound_messages',
      this.getRandomizedOutboundCommunicationSamples()
    )

    this.preValidateForm('useCaseRegistrationForm')
  },
  computed: {
    ...mapState({
      campaigns: (state) => state.campaigns
    }),
    ...mapState(['easy_wizard', 'unregistered_lines']),

    privacy_policy_url: {
      get() {
        return this.useCaseRegistrationForm.privacy_policy_url
      },
      set(value) {
        this.useCaseRegistrationForm.privacy_policy_url = this.transformUrl(value)
      }
    },

    terms_and_conditions_url: {
      get() {
        return this.useCaseRegistrationForm.terms_and_conditions_url
      },
      set(value) {
        this.useCaseRegistrationForm.terms_and_conditions_url = this.transformUrl(value)
      }
    },

    selectableUseCases() {
      // Show all use cases when the AI panel has surfaced issues — the AI
      // may suggest a use case that isn't in the easy wizard's short list.
      const aiHasIssues = this.aiPanel?.result?.report?.issues?.length > 0
      if (this.easy_wizard && !aiHasIssues) {
        let useCasesToFilter = [
          USE_CASE_CUSTOMER_CARE,
          USE_CASE_MARKETING,
          USE_CASE_MIXED
        ]
        return this.filterUseCases(useCasesToFilter)
      }
      return UseCases
    },

    shouldCollapseLines() {
      if (this.useCaseRegistrationForm.line_ids.length < 6) {
        return false
      }
      return true
    },

    shouldDisableForm() {
      // Disable if campaign has ineligible errors
      if (this.hasIneligibleErrors) {
        return true
      }

      // Disable if Pre-vetting is pending
      if (
        this.selected_messaging_service?.a2p_use_case?.prevetting_status ===
        ComplianceStatuses.STATUS_PENDING
      ) {
        return true
      }

      // Disable if A2P Campaign is in review or approved by Twilio using .includes()
      if (
        [
          ComplianceStatuses.STATUS_IN_REVIEW,
          ComplianceStatuses.STATUS_APPROVED
        ].includes(this.selected_messaging_service?.a2p_use_case?.status)
      ) {
        return true
      }

      return false
    },

    isSuspendedOrRejected() {
      return [
        ComplianceStatuses.STATUS_SUSPENDED,
        ComplianceStatuses.STATUS_REJECTED
      ].includes(this.selected_messaging_service?.a2p_use_case?.status)
    },

    isSuspended() {
      return (
        this.selected_messaging_service?.a2p_use_case?.status ===
        ComplianceStatuses.STATUS_SUSPENDED
      )
    },

    shouldShowUnregisteredLines() {
      return !!this.selected_messaging_service?.a2p_use_case
    },

    hasIneligibleErrors() {
      const errors = this.selected_messaging_service?.a2p_use_case?.errors || []
      return errors.some((error) =>
        INELIGIBLE_ERROR_CODES.includes(error.error_code)
      )
    },

    ineligibleErrorCodesFound() {
      const errors = this.selected_messaging_service?.a2p_use_case?.errors || []
      return errors
        .filter((error) => INELIGIBLE_ERROR_CODES.includes(error.error_code))
        .map((error) => error.error_code)
    },

    hasCampaignIssues() {
      return (
        this.selected_messaging_service?.a2p_use_case &&
        (this.selected_messaging_service?.a2p_use_case?.prevetting_status ===
          ComplianceStatuses.STATUS_REJECTED ||
          this.selected_messaging_service?.a2p_use_case?.errors?.length > 0 ||
          this.isSuspendedOrRejected)
      )
    },

    campaignStatusTitle() {
      if (this.hasIneligibleErrors) {
        return "Your A2P Campaign has been rejected and it's ineligible for resubmission"
      } else if (
        this.selected_messaging_service?.a2p_use_case?.status ===
        ComplianceStatuses.STATUS_SUSPENDED
      ) {
        return 'Your A2P Campaign has been suspended'
      } else {
        return 'Your A2P Campaign has been rejected'
      }
    },

    campaignAlertType() {
      if (this.hasIneligibleErrors) {
        return 'error'
      } else if (
        this.selected_messaging_service?.a2p_use_case?.status ===
        ComplianceStatuses.STATUS_SUSPENDED
      ) {
        return 'warning'
      } else {
        return 'error'
      }
    },

    monthlyChargeAmount() {
      // Check if it's a low volume mixed campaign
      const useCase = this.selected_messaging_service?.a2p_use_case?.use_case

      if (useCase === USE_CASE_LOW_VOLUME) {
        return '$1.50 USD'
      }

      return '$10 USD'
    },

    analyzeButtonLabel() {
      if (!this.aiPanel.analyzed) return 'Analyze with AloAi'
      return 'Re-analyze'
    },

    isAnalyzeDisabled() {
      if (this.aiPanel.loading) return true
      const noIssues = this.aiPanel.analyzed && !this.aiPanel.hasIssues
      return noIssues && !this.formChangedSinceAnalysis
    },

    isFinishDisabled() {
      if (!this.aiPanel.analyzed) return true
      if (!this.aiPanel.hasIssues) return false
      return !this.aiPanel.allResolved
    },

    finishDisabledTooltip() {
      if (!this.aiPanel.analyzed) return 'Run AloAi analysis before submitting'
      if (!this.aiPanel.hasIssues) return ''
      return 'Mark all issues as resolved before submitting'
    }
  },
  methods: {
    addLine(line_ids) {
      this.preValidateForm('useCaseRegistrationForm')

      // Sanity check
      if (!line_ids) {
        return
      }

      this.useCaseRegistrationForm.line_ids = [...line_ids]
    },
    runAiPreCheck() {
      this.aiPanel.visible = true
      this.aiPanel.loading = true
      this.aiPanel.result = null
      this.aiPanel.error = false
      this.aiPanel.errorMessage = null

      axios
        .post(
          '/api/v1/compliance/a2p-use-cases/ai-pre-check',
          this.useCaseRegistrationForm
        )
        .then((response) => {
          this.aiPanel.loading = false
          this.aiPanel.result = response.data
          this.aiPanel.analyzed = true
          this.aiPanel.hasIssues =
            (response.data?.report?.issues?.length ?? 0) > 0
          this.formChangedSinceAnalysis = false
        })
        .catch((error) => {
          this.aiPanel.loading = false
          this.aiPanel.error = true
          this.aiPanel.analyzed = true
          this.aiPanel.hasIssues = false
          this.formChangedSinceAnalysis = false
          this.aiPanel.errorMessage =
            error?.response?.status === 429
              ? 'You\'ve run too many checks. Wait 1 minute, then click Retry to try again.'
              : null
        })
    },
    openUseCaseRegistrationConfirmation() {
      if (this.aiPanel.hasIssues) {
        this.$confirm(
          'We recommend re-running the AloAi analysis to confirm your changes pass before submitting.',
          'Run another check?',
          {
            confirmButtonText: 'Re-analyze',
            cancelButtonText: 'Submit anyway',
            customClass: 'w-25 fixed',
            type: 'warning'
          }
        )
          .then(() => {
            this.runAiPreCheck()
          })
          .catch(() => {
            this.submitUseCaseRegistrationForm()
          })
        return
      }

      this.$confirm(
        'Are you sure you want to register this A2P Use Case?',
        'Confirm Submission',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          customClass: 'w-25 fixed',
          confirmButtonClass: 'el-button--success',
          type: 'info'
        }
      )
        .then(() => {
          this.submitUseCaseRegistrationForm()
        })
        .catch(() => {
          // User cancelled
        })
    },
    submitUseCaseRegistrationForm() {
      this.preValidateForm('useCaseRegistrationForm')

      if (!this.validateForm('useCaseRegistrationForm')) {
        return
      }

      this.loading = true

      const payload = {
        ...this.useCaseRegistrationForm,
        ai_validation_report_id: this.aiPanel.result?.report?.report_id ?? null
      }

      axios
        .post(
          `/api/v1/compliance/messaging-services/${this.selected_messaging_service?.id}/a2p-use-case`,
          payload
        )
        .then((response) => {
          this.loading = false
          this.$message({
            showClose: true,
            message:
              'A2P Campaign Registration form has been submitted successfully. We will notify you about its status.',
            type: 'success'
          })

          this.resetFormState()
          this.$emit('next', { a2p_use_case: response.data })
          this.submitAlert()
        })
        .catch(({ response }) => {
          this.loading = false
          this.submitAlert(response)
        })
    },
    resetFormState() {
      this.useCaseRegistrationForm = {
        line_ids: [],
        accepted_campaign_fee: false,
        use_case: '',
        description: '',
        sample_message_1: '',
        sample_message_2: '',
        has_embedded_links: false,
        has_embedded_phone: false,
        age_gated: false,
        direct_lending: false,
        agree_terms_and_conditions: false,
        message_flow: '',
        privacy_policy_url: '',
        terms_and_conditions_url: ''
      }
      this.aiPanel = {
        visible: false,
        loading: false,
        result: null,
        error: false,
        analyzed: false,
        hasIssues: false,
        allResolved: false
      }
      this.formChangedSinceAnalysis = false
      this.$nextTick(() => {
        this.$refs.useCaseRegistrationForm?.clearValidate()
      })
    },
    handleAiContinue() {
      this.aiPanel.visible = false
    },
    handleAiDismiss() {
      this.aiPanel.visible = false
    },
    handleAiApplyFix({ field, value }) {
      this.writeAiFieldValue(field, value)
    },
    handleAiUndoFix({ field, value }) {
      // The panel emits undo-fix with the previous value already in `value`,
      // so the write path is the same as apply — we keep a dedicated method
      // purely for intent-clarity at the call site.
      this.writeAiFieldValue(field, value)
    },
    writeAiFieldValue(field, value) {
      if (
        !field ||
        !Object.prototype.hasOwnProperty.call(
          this.useCaseRegistrationForm,
          field
        )
      )
        return

      this.useCaseRegistrationForm[field] = value

      this.$nextTick(() => {
        const target = document.querySelector(`[data-aloai-field="${field}"]`)
        if (!target) return

        target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        target.classList.add('aloai-field-highlight')
        setTimeout(() => target.classList.remove('aloai-field-highlight'), 2000)
      })
    },
    filterUseCases(useCasesToFilter) {
      return UseCases.filter((useCase) =>
        useCasesToFilter.includes(useCase['value'])
      )
    },
    useCaseChanged() {
      if (this.easy_wizard) {
        switch (this.useCaseRegistrationForm.use_case) {
          case USE_CASE_CUSTOMER_CARE:
            // Pre-select lines that have a user_id or only have one incoming number
            const customer_care_line_ids = this.unregistered_lines
              .filter(
                (line) =>
                  !line.has_local_presence &&
                  (line.user_id || line.incoming_numbers.length === 1)
              )
              .map((line) => line.id)

            this.addLine(customer_care_line_ids)
            this.line_ids = [...customer_care_line_ids]
            break
          case USE_CASE_MARKETING:
            // Pre-select all lines containing 10DLC Numbers
            const marketing_line_ids = this.unregistered_lines
              .filter(
                (line) =>
                  !line.has_local_presence && line.incoming_numbers.length > 0
              )
              .map((line) => line.id)

            this.addLine(marketing_line_ids)
            this.line_ids = [...marketing_line_ids]
            break
          case USE_CASE_MIXED:
            // Pre-select lines that have a user_id or only have one incoming number or are has 10DLC Numbers
            let mixed_line_ids = this.unregistered_lines
              .filter(
                (line) =>
                  !line.has_local_presence &&
                  (line.user_id ||
                    line.incoming_numbers.length === 1 ||
                    line.is_10_dlc === true)
              )
              .map((line) => line.id)

            this.addLine(mixed_line_ids)
            this.line_ids = [...mixed_line_ids]
          default:
            break
        }
      }
      this.preValidateForm('useCaseRegistrationForm')
    },
    getRandomizedOutboundCommunicationSamples() {
      // Sanity check
      if (this.unregistered_lines.length === 0) {
        return
      }

      let unregistered_line_ids = this.unregistered_lines.map((line) => line.id)
      axios
        .post('/api/v1/compliance/marketing-samples', { unregistered_line_ids })
        .then((res) => {
          this.communications_messages = res.data
          if (this.communications_messages.length !== 0) {
            this.randomizeCommunicationSamples('sample_message_1')
            this.randomizeCommunicationSamples('sample_message_2')
          }
        })
        .catch(({ error }) => {
          console.log(error)
        })
    },
    randomizeCommunicationSamples(field, tries = 0) {
      // Sanity check
      if (
        !this.communications_messages ||
        this.communications_messages.length === 0
      ) {
        console.log('No sample messages to show.')
        return
      }

      // Get out of recursiveness if all messages have the same body.
      if (tries > this.communications_messages.length) {
        console.log('All sample messages have the same body.')
        return
      }

      // Get a random body from the communications messages
      let random_index = Math.floor(
        Math.random() * this.communications_messages.length
      )
      let random_body = this.communications_messages[random_index]

      // Check if previous body has the same text
      if (this.useCaseRegistrationForm[field] === random_body) {
        // Try to fetch a different body each time it's refreshed
        this.randomizeCommunicationSamples(field, tries + 1)
        return
      }

      this.useCaseRegistrationForm[field] = random_body
    }
  },
  watch: {
    useCaseRegistrationForm: {
      deep: true,
      handler() {
        if (this.aiPanel.analyzed) {
          this.formChangedSinceAnalysis = true
        }
      }
    },
    selected_lines: {
      handler: function (lines) {
        this.line_ids = lines
        if (lines.length === 0) {
          this.useCaseChanged()
        }
      },
      immediate: true
    },
    line_ids: {
      handler: function (campaignIds) {
        this.useCaseRegistrationForm.line_ids = campaignIds
      }
    }
  }
}
</script>

<style scoped>
.aloai-analyze-btn {
  background: linear-gradient(135deg, #9333ea, #6366f1) !important;
  border-color: transparent !important;
  color: #fff !important;
}
.aloai-analyze-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7e22ce, #4f46e5) !important;
}
.aloai-analyze-btn.is-disabled,
.aloai-analyze-btn:disabled {
  background: linear-gradient(135deg, #c4b5fd, #a5b4fc) !important;
  opacity: 0.7;
}

/* AloAi applied-fix highlight — pulses a purple glow then fades */
:deep(.aloai-field-highlight) {
  border-radius: 6px;
  animation: aloai-field-flash 2s ease forwards;
}

@keyframes aloai-field-flash {
  0% {
    box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.6);
    background: rgba(147, 51, 234, 0.06);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(147, 51, 234, 0.35);
    background: rgba(147, 51, 234, 0.04);
  }
  100% {
    box-shadow: 0 0 0 0px rgba(147, 51, 234, 0);
    background: transparent;
  }
}
</style>
